import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginWithEmailAndPassword, loginWithGoogle } from '../../firebase/auth';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const { setUser } = useAuth(); // Ajouter cette ligne pour accéder au contexte d'authentification
  
  // Get the redirect path from location state or use default
  const from = location.state?.from?.pathname || "/app/adblock-bypass";
  

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAuthError(null);

    try {
      await loginWithEmailAndPassword(email, password);
      // Ajouter la redirection après une connexion réussie
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Erreur de connexion:', error);
      setAuthError(error?.message || 'Erreur lors de la connexion');
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setAuthError(null);
    
    try {
      const user = await loginWithGoogle(); // Récupérer l'utilisateur retourné
      
      // Mettre à jour le contexte d'authentification avec l'utilisateur
      setUser(user);
      
      // Protection CSRF
      const csrfToken = Math.random().toString(36).substring(2);
      sessionStorage.setItem('csrfToken', csrfToken);
      
      // Rediriger vers le dashboard ou la page demandée
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Erreur de connexion Google:', error);
      setAuthError(error?.message || 'Erreur lors de la connexion avec Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-md space-y-8 rounded-md bg-background border-border border p-8 shadow-lg">
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-foreground">
          Se connecter
        </h2>

        {/* Formulaire de connexion existant */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="sr-only">
              Adresse e-mail
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="relative block w-full appearance-none rounded-md border border-input px-3 py-2 text-foreground bg-background placeholder-muted-foreground focus:z-10 focus:border-primary focus:outline-none focus:ring-primary"
              placeholder="Adresse e-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="password" className="sr-only">
              Mot de passe
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              className="relative block w-full appearance-none rounded-md border border-input px-3 py-2 text-foreground bg-background placeholder-muted-foreground focus:z-10 focus:border-primary focus:outline-none focus:ring-primary"
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div>
            <button
              type="submit"
              className="flex w-full justify-center rounded-md border border-transparent bg-primary text-primary-foreground py-2 px-4 text-sm font-medium shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              Se connecter
            </button>
          </div>
        </form>

        {/* Séparateur */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-background px-2 text-muted-foreground">Ou</span>
          </div>
        </div>

        {/* Bouton de connexion Google */}
        <Button
          type="button"
          variant="outline"
          className="w-full flex items-center justify-center"
          onClick={handleGoogleLogin}
          disabled={googleLoading}
        >
          {googleLoading ? (
            <span>Connexion...</span>
          ) : (
            <>
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Se connecter avec Google
            </>
          )}
        </Button>

        {/* Affichage des erreurs */}
        {authError && (
          <div className="mt-4 text-center text-sm text-destructive">
            {authError}
          </div>
        )}
      </div>
    </div>
  );
};